import React, { useState, useEffect, useCallback } from 'react';
import { SafeAreaView, View, FlatList, ActivityIndicator, AsyncStorage } from 'react-native';
import { Text, Card, Icon, Button } from 'react-native-elements';
import { Tools } from '../../utils/types/Tools';
import { connect } from 'react-redux';
import { RootState } from '../../redux';
import { User } from '../../utils/types/Users';
import Firebase from '../../components/FirebaseContext';
import { SuccessToast, FailureToast } from '../../utils';
import 'react-datepicker/dist/react-datepicker.css';
import { fstHelper } from '../../utils/types/others';
import { colors } from '../../utils/constants';
import { Menu, MenuTrigger, MenuOptions, MenuOption, renderers } from 'react-native-popup-menu';
import { StackScreenProps } from '@react-navigation/stack';
import { ToolStackParamList } from './ToolStack';
import { useFocusEffect } from '@react-navigation/native';

type ToolManagerProp = StackScreenProps<ToolStackParamList, 'Main'>;

function ToolManager({ navigation }: ToolManagerProp): JSX.Element {
	const firestore = Firebase.firestore();
	const [loading, setLoading] = useState(false);
	const [tools, setTools] = useState<Array<{ docId: string; tool: Tools }>>([]);
	const [managers, setManagers] = useState<Array<User>>([]);
	useEffect(() => {
		//load managers
		firestore
			.collection('users')
			.where('role', 'in', ['Admin', 'QA Manager'])
			.get()
			.then((res) => {
				if (!res.empty) {
					const m = res.docs.map((m) => m.data() as User);
					setManagers(m);
				}
			});
	}, [firestore]);

	const [refresh, setRefresh] = useState<string>('1');
	useEffect(() => {
		//load all existing projects order by date created desc
		firestore
			.collection('tools')
			.orderBy('createdAt', 'desc')
			.get()
			.then((res) => {
				if (!res.empty) {
					const t = res.docs.map((t) => {
						return { docId: t.id, tool: t.data() as Tools };
					});
					setTools(t);
				}
			});
	}, [firestore, refresh]);

	useFocusEffect(
		useCallback(() => {
			firestore
				.collection('tools')
				.orderBy('createdAt', 'desc')
				.get()
				.then((res) => {
					if (!res.empty) {
						const t = res.docs.map((t) => {
							return { docId: t.id, tool: t.data() as Tools };
						});
						setTools(t);
					}
				});
		}, [firestore]),
	);

	return (
		<SafeAreaView style={{ flex: 1, flexDirection: 'row', justifyContent: 'center' }}>
			<View style={{ flex: 0.6 }}>
				<Text style={{ fontWeight: '700', marginBottom: 20, textAlign: 'center', marginTop: 20 }} h3>
					Tools
				</Text>
				<View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
					<ActivityIndicator animating={loading} size={28} style={{ left: 20 }} />
					<Button
						title="New Tool"
						onPress={() => navigation.navigate('Create', { toolId: 'new' })}
						containerStyle={{ right: 20 }}
						buttonStyle={{ paddingVertical: 4 }}
						titleStyle={{ fontSize: 12 }}
					/>
				</View>
				<FlatList
					data={tools}
					renderItem={({ item }) => {
						const { docId, tool } = item;
						return (
							<Card>
								<Text h4>
									{tool.name} | {tool.id}
								</Text>
								<Text style={{ maxWidth: 600 }}>{tool.description}</Text>
								<Text>
									Created By: {managers.find((m) => m.uid == tool.createdBy)?.firstName}{' '}
									{managers.find((m) => m.uid == tool.createdBy)?.lastName}
								</Text>
								<Text>Created On: {new Date((tool.createdAt as fstHelper).toDate()).toDateString()}</Text>
								<View style={{ flexDirection: 'row', position: 'absolute', right: 0 }}>
									<Icon
										reverse
										name="edit-3"
										type="feather"
										size={16}
										color={colors.green}
										onPress={() => {
											setLoading(true);
											// tool guard, deny if part of an ongoing project
											firestore
												.collection('projects')
												.where('tools', 'array-contains', tool.id)
												.where('stopTime', '>', new Date())
												.get()
												.then((res) => {
													setLoading(false);
													if (res.size) {
														FailureToast('This tool is assigned to a project');
														return;
													}
													// cast serverTimeStamp toDate before stringify
													const nomutateTool = Object.assign({}, tool);
													nomutateTool.createdAt = (nomutateTool.createdAt as fstHelper).toDate();
													AsyncStorage.setItem(docId, JSON.stringify(nomutateTool)).then(() => {
														setLoading(false);
														navigation.navigate('Create', { toolId: docId });
													});
												})
												.catch(() => {
													setLoading(false);
												});
										}}
									/>
									<Menu renderer={renderers.Popover} rendererProps={{ preferredPlacement: 'bottom' }}>
										<MenuTrigger>
											<Icon reverse name="trash-2" type="feather" size={16} color={colors.red} />
										</MenuTrigger>
										<MenuOptions customStyles={{ optionsWrapper: { padding: 10 } }}>
											<Text style={{ color: colors.darkRed, fontWeight: '700' }}>This action is irreversible!</Text>
											<View style={{ flexDirection: 'row', justifyContent: 'space-around', marginVertical: 16 }}>
												<MenuOption
													text="Cancel"
													customStyles={{
														optionWrapper: { backgroundColor: colors.blue, borderRadius: 4, paddingHorizontal: 6 },
														optionText: { color: '#fff' },
													}}
												/>
												<MenuOption
													text="Yes delete"
													customStyles={{
														optionWrapper: { backgroundColor: colors.red, borderRadius: 4, paddingHorizontal: 6 },
														optionText: { color: '#fff' },
													}}
													onSelect={async () => {
														firestore
															.collection('projects')
															.where('tools', 'array-contains', tool.id)
															.where('stopTime', '>', new Date())
															.get()
															.then(async (res) => {
																setLoading(false);
																if (res.size) {
																	FailureToast('This tool is assigned to an active project');
																	return;
																}
																await firestore.collection('tools').doc(docId).delete();
																SuccessToast(`Tool ${tool.id} has been deleted!`);
																setRefresh(Date.now().toString());
															})
															.catch((e) => {
																console.log(Object.keys(e));
															});
													}}
												/>
											</View>
										</MenuOptions>
									</Menu>
								</View>
							</Card>
						);
					}}
					keyExtractor={(item) => item.tool.id}
					ListFooterComponent={<View style={{ height: 60 }}></View>}
				/>
			</View>
		</SafeAreaView>
	);
}

const mapStateToProps = (state: RootState) => {
	const { user } = state;
	return {
		user,
	};
};

export default connect(mapStateToProps)(ToolManager);
